import classNames from "classnames";
import { Button } from "@/components";
import type { ApiDevice } from "../../_types/devices.types";
import { DeviceListItem } from "../device-list-item";
import styles from "./DevicesList.module.scss";

type DevicesListProps = {
  devices: ApiDevice[];
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onDeviceClick: (id: string) => void;
  onEditDevice: (device: ApiDevice) => void;
  onRequestDeleteDevice: (device: ApiDevice) => void;
  actionsDisabled: boolean;
};

export const DevicesList = ({
  devices,
  isLoading,
  isFetching,
  isError,
  onRetry,
  onDeviceClick,
  onEditDevice,
  onRequestDeleteDevice,
  actionsDisabled,
}: DevicesListProps) => {
  if (isLoading) {
    return (
      <div className={styles["comp__state"]}>
        <div className={styles["comp__spinner"]} />
        <div>Loading devices…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles["comp__state"]}>
        <div>Could not load devices.</div>
        {onRetry ? (
          <Button size="small" variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className={styles["comp__state"]}>
        {isFetching ? "Updating…" : "No devices match your filters."}
      </div>
    );
  }

  return (
    <div className={styles["comp"]} aria-busy={isFetching}>
      <div className={styles["comp__table-header"]}>
        <div className={classNames(styles["comp__column"], styles["comp__column--person"])}>
          Device
        </div>
        <div className={classNames(styles["comp__column"], styles["comp__column--category"])}>
          Type
        </div>
        <div className={classNames(styles["comp__column"], styles["comp__column--model"])}>
          Model / OS
        </div>
        <div className={classNames(styles["comp__column"], styles["comp__column--content"])}>
          Notes
        </div>
        <div className={classNames(styles["comp__column"], styles["comp__column--date"])}>
          Last seen
        </div>
        <div className={classNames(styles["comp__column"], styles["comp__column--actions"])}>Actions</div>
      </div>

      <div className={styles["comp__list"]}>
        {devices.map((device) => (
          <DeviceListItem
            key={device.id}
            device={device}
            onRowClick={() => onDeviceClick(device.id)}
            onEdit={() => onEditDevice(device)}
            onRequestDelete={() => onRequestDeleteDevice(device)}
            actionsDisabled={actionsDisabled}
          />
        ))}
      </div>
    </div>
  );
};
